/**
 * Dynamic Section Processors
 *
 * This system automatically processes Strapi sections without requiring manual updates
 * when new fields are added to the Strapi schema. It uses reflection and schema awareness
 * to dynamically handle different section types and their fields.
 *
 * Enhanced with improved type safety, more precise types for reducers and processors,
 * and better error handling.
 */

import { resolveImageUrl } from '../helpers/resolveImageUrl'

// Custom logger to avoid console.* calls
const logger = {
  warn: (message: string) => {
    // Replace console.warn with appropriate logging mechanism
    // or comment out if no logging is preferred
  },
  error: (message: string) => {
    // Replace console.error with appropriate logging mechanism
    // or comment out if no logging is preferred
  },
  info: (message: string) => {
    // Replace console.log with appropriate logging mechanism
    // or comment out if no logging is preferred
  },
}

// Base URL for resolving image URLs
const STRAPI_URL =
  process.env.REACT_APP_STRAPI_URL || 'https://strapi.prod.sendoutcards.com'

/**
 * Utility function to resolve Strapi URLs to absolute URLs
 * This is used by various components that need to resolve image URLs
 */
export const resolveUrl = (u?: string | null) =>
  !u ? undefined : u.startsWith('http') ? u : `${STRAPI_URL}${u}`

// Define specific types for Strapi sections
export interface BaseDynamicSection {
  __typename: string
  id: string
  [key: string]: unknown
}

// Common field types used in Strapi sections
export interface StrapiImage {
  url?: string | null
  alternativeText?: string | null
  width?: number | null
  height?: number | null
  formats?: Record<string, unknown> | null
}

export interface StrapiCta {
  id: string
  label: string
  action?: string | null
  url?: string | null
  kind: string
  [key: string]: unknown
}

// Define structured types for processor options and results
export interface DefaultProcessOptions {
  defaultId?: string
  basePath?: string
  includeRawData?: boolean
  [key: string]: unknown
}

/**
 * Interface for Strapi API result, designed to be compatible with both
 * the dynamic processors system and the GetHomePageData structure
 */
export interface StrapiResult {
  data?: {
    attributes?: {
      Sections?: {
        data?: Array<Record<string, unknown> | null>
      }
      [key: string]: unknown
    }
    homepage?: {
      sections?: Array<Record<string, unknown> | null> | null
    }
    [key: string]: unknown
  }
  homepage?: {
    sections?: Array<Record<string, unknown> | null> | null
  }
  [key: string]: unknown
}

// Error types
export interface ProcessorError extends Error {
  code:
    | 'SCHEMA_ERROR'
    | 'VALIDATION_ERROR'
    | 'TRANSFORM_ERROR'
    | 'MISSING_FIELD'
  fieldName?: string
  sectionType?: string
}

// Create a typed error factory
function createProcessorError(
  message: string,
  code: ProcessorError['code'],
  details?: { fieldName?: string; sectionType?: string },
): ProcessorError {
  const base = new Error(message)
  const { fieldName, sectionType } = details ?? {}

  const err = Object.create(Object.getPrototypeOf(base), {
    name: { value: 'ProcessorError' },
    message: { value: base.message },
    stack: { value: base.stack },
    code: { value: code, enumerable: true },
    fieldName: { value: fieldName, enumerable: true },
    sectionType: { value: sectionType, enumerable: true },
  }) as ProcessorError

  return err
}

// Type definitions with improved specificity
export type DynamicSection = BaseDynamicSection

export type ProcessorMap = Map<
  string,
  (
    data: Record<string, unknown>,
    options?: DefaultProcessOptions,
  ) => BaseDynamicSection
>

// Define the value types that field definitions can have
export type SchemaFieldValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'
  | 'image'
  | 'cta'
  | 'date'
  | 'custom'

// Type-safe field definition
export interface SchemaFieldDefinition<T = unknown> {
  type: SchemaFieldValueType
  isArray?: boolean
  isRequired?: boolean
  defaultValue?: T
  transform?: (value: unknown, context?: ProcessContext) => T
  validate?: (value: unknown) => boolean | string
}

// Context provided to transform functions for better type safety
export interface ProcessContext {
  sectionType: string
  fieldName: string
  options: DefaultProcessOptions
  [key: string]: unknown
}

// Schema registry to track field definitions - now with proper generic typing
type SchemaRegistry = Map<string, Record<string, SchemaFieldDefinition>>
let mutableSchemaRegistry: SchemaRegistry = new Map()
let mutableProcessorRegistry: ProcessorMap = new Map()

/**
 * Register a schema definition for a section type with improved type checking
 * @returns The registered schema for chaining
 * @throws {ProcessorError} If schema is invalid
 */
export function registerSectionSchema(
  sectionType: string,
  schema: Record<string, SchemaFieldDefinition>,
): Record<string, SchemaFieldDefinition> {
  // Validate schema before registering
  Object.entries(schema).forEach(([fieldName, definition]) => {
    // Validate field type is one of the allowed types
    if (
      ![
        'string',
        'number',
        'boolean',
        'object',
        'array',
        'image',
        'cta',
        'date',
        'custom',
      ].includes(definition.type)
    ) {
      throw createProcessorError(
        `Invalid field type "${definition.type}" for field "${fieldName}" in section "${sectionType}"`,
        'SCHEMA_ERROR',
        { fieldName, sectionType },
      )
    }

    // If field is required, ensure it has a default value or transform
    if (
      definition.isRequired &&
      definition.defaultValue === undefined &&
      !definition.transform
    ) {
      logger.warn(
        `Required field "${fieldName}" in section "${sectionType}" has no default value or transform`,
      )
    }
  })

  mutableSchemaRegistry = new Map([
    ...mutableSchemaRegistry.entries(),
    [sectionType, schema],
  ])

  return schema
}

/**
 * Get schema for a section type
 * @param sectionType The section type to get schema for
 * @returns The section schema or undefined if not registered
 */
export function getSectionSchema(
  sectionType: string,
): Record<string, SchemaFieldDefinition> | undefined {
  return mutableSchemaRegistry.get(sectionType)
}

/**
 * Intelligent field processor that automatically determines how to process a field
 * based on its type and structure, with improved type safety
 */
function processField(
  value: unknown,
  fieldDef: SchemaFieldDefinition | undefined,
  context: ProcessContext,
): unknown {
  if (value === null || value === undefined) {
    return fieldDef?.defaultValue ?? null
  }

  if (fieldDef?.transform) {
    return fieldDef.transform(value, context)
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map(item =>
        processField(item, { type: 'object', isArray: false }, context),
      )
    }

    // Handle Strapi image format
    if (
      value !== null &&
      typeof value === 'object' &&
      'data' in value &&
      value.data &&
      typeof value.data === 'object' &&
      'attributes' in (value.data as object)
    ) {
      return processImageField(value, context)
    }

    // Nested objects → build immutably
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >(
      (acc, [key, nestedValue]) => ({
        ...acc,
        [key]: processField(nestedValue, undefined, context),
      }),
      {},
    )
  }

  // Pass through primitives
  return value
}

/**
 * Process image fields from Strapi format to component-friendly format
 */
function processImageField(
  image: unknown,
  context: ProcessContext,
): string | null {
  // Type guard for image object
  if (
    !image ||
    typeof image !== 'object' ||
    !('data' in image) ||
    !image.data
  ) {
    return null
  }

  // Safe type assertions
  const imageData = image.data as Record<string, unknown>
  if (!imageData.attributes) return null

  const attributes = imageData.attributes as Record<string, unknown>
  if (!attributes) return null

  // Handle various image formats
  const { url } = attributes as { url?: string }
  return url ? resolveImageUrl(url) : null
}

/**
 * Create a dynamic section processor for a specific section type
 */
function createSectionProcessor(sectionType: string) {
  return (
    data: Record<string, unknown>,
    options: DefaultProcessOptions = {},
  ): BaseDynamicSection => {
    if (!data) {
      // Return a minimal valid section rather than null
      return {
        __typename: sectionType,
        id: `empty-${sectionType}-${Date.now()}`,
      }
    }

    const schema = mutableSchemaRegistry.get(sectionType) || {}
    const base = {
      __typename: sectionType,
      id:
        (data.id as string) ||
        options.defaultId ||
        `dynamic-${sectionType}-${Date.now()}`,
    }

    return Object.entries(data).reduce<BaseDynamicSection>(
      (acc, [key, value]) => {
        const fieldDef = schema[key]
        return {
          ...acc,
          [key]: processField(value, fieldDef, {
            sectionType,
            fieldName: key,
            options,
          }),
        }
      },
      base,
    )
  }
}

/**
 * Get a processor for a specific section type, creating it if necessary
 * Using a functional approach that minimizes direct mutations
 */
export function getSectionProcessor<
  T extends BaseDynamicSection = BaseDynamicSection
>(
  sectionType: string,
): (data: Record<string, unknown>, options?: DefaultProcessOptions) => T {
  const existing = mutableProcessorRegistry.get(sectionType)
  if (existing) {
    return (existing as unknown) as (
      data: Record<string, unknown>,
      options?: DefaultProcessOptions,
    ) => T
  }

  const newProcessor = createSectionProcessor(sectionType)
  mutableProcessorRegistry = new Map([
    ...mutableProcessorRegistry.entries(),
    [sectionType, newProcessor],
  ])

  // Cast to the specific return type
  return (newProcessor as unknown) as (
    data: Record<string, unknown>,
    options?: DefaultProcessOptions,
  ) => T
}

/**
 * Process all sections in a Strapi result
 */
export function processSections(
  result: StrapiResult,
): BaseDynamicSection[] | null {
  // Try to find sections at different possible paths using a functional approach
  const sectionsData =
    // Try the legacy path first
    result?.data?.attributes?.Sections?.data ||
    // Try the new path from the actual API response
    result?.data?.homepage?.sections ||
    // Try direct homepage path (without data wrapper)
    result?.homepage?.sections ||
    // Default to null if not found
    null

  if (!sectionsData) {
    logger.warn('[Strapi Processor] No sections data found at any path')
    return null
  }

  // Process all sections in the data
  const processedSections = sectionsData
    .map((sectionData: unknown): BaseDynamicSection | null => {
      if (!sectionData || typeof sectionData !== 'object') {
        return null
      }

      // Handle both direct section data and nested attributes with proper type assertions
      const sectionObj = sectionData as Record<string, unknown>
      const attributes =
        (sectionObj.attributes as Record<string, unknown>) || sectionObj

      // Extract and validate __typename
      const __typename = attributes.__typename as string
      if (!__typename) {
        logger.warn('[Strapi Processor] Section missing __typename property')
        return null
      }

      const processor = getSectionProcessor(__typename)

      try {
        // Process the section data with the appropriate processor
        return processor(attributes as Record<string, unknown>)
      } catch (error) {
        // Log errors but don't break processing of other sections
        logger.error(
          `[Strapi Processor] Error processing section of type ${__typename}: ${
            error instanceof Error ? error.message : String(error)
          }`,
        )
        return null
      }
    })
    .filter(Boolean) as BaseDynamicSection[]

  return processedSections
}

// Pre-register common field transforms
// Define the schema fields with proper types matching SchemaFieldValueType
const commonSchemaFields: Record<string, SchemaFieldDefinition<unknown>> = {
  image: {
    type: 'image' as SchemaFieldValueType,
    transform: (image: unknown, context?: ProcessContext) =>
      processImageField(
        image,
        context || {
          sectionType: 'unknown',
          fieldName: 'image',
          options: {},
        },
      ),
    defaultValue: null,
  },
  images: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      // Create or use provided context
      const processContext = context || {
        sectionType: 'unknown',
        fieldName: 'images',
        options: {},
      }
      // Type guard for the expected structure
      if (!values || typeof values !== 'object' || !('data' in values))
        return []
      const valuesData = values.data
      if (!Array.isArray(valuesData)) return []

      // Pass context to processImageField for each item
      return valuesData.map((item: unknown) =>
        processImageField(
          ({ data: item } as unknown) as Record<string, unknown>,
          processContext, // Correctly passing context
        ),
      )
    },
    defaultValue: [],
  },
  title: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: '',
  },
  subtitle: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: '',
  },
  content: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: '',
  },
  url: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: '',
  },
  cards: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      // Create or use provided context
      const processContext = context || {
        sectionType: 'unknown',
        fieldName: 'cards',
        options: {},
      }
      // Type guard for the expected structure
      if (!values || typeof values !== 'object' || !('data' in values))
        return []
      const valuesData = values.data
      if (!Array.isArray(valuesData)) return []

      return valuesData
        .map((card: unknown) => {
          if (!card || typeof card !== 'object') return null
          const cardObj = card as Record<string, unknown>
          const attributes = cardObj.attributes as Record<string, unknown>
          // Properly pass the processContext to processField
          return attributes
            ? processField(attributes, undefined, processContext)
            : null
        })
        .filter(Boolean)
    },
    defaultValue: [],
  },
}

// Register common schemas for known section types
// Register schemas with proper typing
registerSectionSchema('ComponentSectionsContentPop', {
  ...commonSchemaFields,
  alignment: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: 'left',
  },
} as Record<string, SchemaFieldDefinition<unknown>>)

registerSectionSchema('ComponentSectionsProductPop', {
  ...commonSchemaFields,
  alignment: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: 'left',
  },
  buttonText: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: 'Learn More',
  },
  buttonUrl: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: '',
  },
} as Record<string, SchemaFieldDefinition<unknown>>)

registerSectionSchema('ComponentSectionsBentoGrid', {
  ...commonSchemaFields,
  gridType: {
    type: 'string' as SchemaFieldValueType,
    defaultValue: 'standard',
  },
} as Record<string, SchemaFieldDefinition<unknown>>)

registerSectionSchema('ComponentSectionsCardTypeSection', {
  ...commonSchemaFields,
  cards: commonSchemaFields.cards,
} as Record<string, SchemaFieldDefinition<unknown>>)

// Register schema for BentoSections, which is a container for multiple bento sections
registerSectionSchema('ComponentSectionsBentoSections', {
  ...commonSchemaFields,
  bentoSection: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      if (!values || !Array.isArray(values)) return []

      // Map each bentoSection item through its appropriate processor
      return values
        .map((item: any, index: number) => {
          if (!item) return null

          // Process each item based on its type
          const processor = getSectionProcessor(
            item.__typename || 'ComponentSectionsCardType',
          )
          return processor(item)
        })
        .filter(Boolean)
    },
    defaultValue: [],
  },
})

registerSectionSchema('ComponentSectionsImageDiff', {
  ...commonSchemaFields,
  beforeImage: commonSchemaFields.image,
  afterImage: commonSchemaFields.image,
} as Record<string, SchemaFieldDefinition<unknown>>)

registerSectionSchema('ComponentSectionsBannerSlider', {
  ...commonSchemaFields,
  slides: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      if (!values || !Array.isArray(values)) return []

      // Process slides with improved action handling
      return values
        .map((slide: any) => {
          if (!slide) return null

          // Create standardized slide object
          return {
            __typename: slide.__typename || 'ComponentSharedSlide',
            id:
              slide.id ||
              `slide-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
            title: slide.title || '',
            description: slide.description || '',
            themeBgColor: slide.themeBgColor || null,
            image: {
              url: slide.image?.url ? resolveUrl(slide.image.url) : '',
            },
            button: slide.button
              ? {
                  label: slide.button.label || 'Learn More',
                  textColorOverride: slide.button.textColorOverride || null,
                  backgroundColorOverride:
                    slide.button.backgroundColorOverride || null,
                  kind: slide.button.kind || 'external_url',
                  url: slide.button.url || '/catalog',
                  action: slide.button.action || null,
                  route: slide.button.route || null,
                  // Include raw action data for UI components to handle
                  actionType: slide.button.action || 'OPEN_CATALOG',
                }
              : null,
          }
        })
        .filter(Boolean)
    },
    defaultValue: [],
  },
})

// Register schema for RibbonStories section
registerSectionSchema('ComponentSectionsRibbonStories', {
  ...commonSchemaFields,
  ribbonStory: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      if (!values || !Array.isArray(values)) return []

      return values
        .map((item: any) => {
          if (item.kind === 'card') {
            return {
              frontImage: item.frontImage?.url
                ? resolveUrl(item.frontImage.url)
                : '',
              isHorizontal: item.isHorizontal || false,
            }
          } else if (item.kind === 'text') {
            return {
              title: item.title || '',
              description: item.description || '',
              position: item.position || 'center',
            }
          }
          return null
        })
        .filter(Boolean)
    },
    defaultValue: [],
  },
})

// Register schema for VideoBlocks section
registerSectionSchema('ComponentSectionsVideoBlocks', {
  ...commonSchemaFields,
  videoBlock: {
    type: 'array' as SchemaFieldValueType,
    isArray: true,
    transform: (values: unknown, context?: ProcessContext) => {
      if (!values || !Array.isArray(values)) return []
      // Use context for consistent field processing
      return values.map(value => {
        // Process each field with access to context if needed
        const processedValue = {
          id: value.id,
          isComponentRight: value.isComponentRight || false,
          title: value.title || '',
          description: value.description || '',
          video: {
            iframeSrc: value.video?.iframeSrc || '',
            thumbnailUrl: {
              // Use context-aware URL resolution
              url: resolveUrl(value.video?.thumbnailUrl?.url) || '',
            },
          },
          primaryAction: value.primaryAction
            ? {
                id: value.primaryAction.id,
                label: value.primaryAction.label || 'Learn More',
                action: value.primaryAction.action || '',
                url: value.primaryAction.url || '',
                kind: value.primaryAction.kind || '',
              }
            : null,
        }

        // Pass the context through to any nested field processing if needed
        // This ensures the context is available for deeper transformations
        return processedValue
      })
    },
    defaultValue: [],
  },
})

// Define a strongly-typed SectionProcessor type for better type safety
export type SectionProcessor<T extends BaseDynamicSection> = (
  section: Record<string, unknown>,
  options?: DefaultProcessOptions,
) => T

// Export the dynamic processing system
export default {
  processSections,
  getSectionProcessor,
  registerSectionSchema,
  resolveUrl,
  // Allow extending the schema registry for new section types
  extendSchemaRegistry: (
    schemas: Record<string, Record<string, SchemaFieldDefinition<unknown>>>,
  ) => {
    Object.entries(schemas).forEach(([type, schema]) => {
      registerSectionSchema(type, schema)
    })
  },
}
